import { Observable, Subject } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { Component, OnInit, ViewEncapsulation } from '@angular/core';
import { IPageResult } from '../../interfaces/page-result';
import { IVATRate } from '../../interfaces/vatrate';
import { IVATRateFilter } from '../../interfaces/vatrate-filter';
import { VATRateService } from '../../services';

import { InputHelper } from '../../utils/input.helper';
import { LazyLoadEvent, CheckboxModule, Table } from 'primeng';
import { UntypedFormControl, UntypedFormGroup, Validators, FormBuilder } from '@angular/forms';
import moment from 'moment';

@Component({
  selector: 'vat-rates',
  templateUrl: './manage-vatrate.component.html',
  styleUrls: ['./manage-vatrate.component.css'],
  encapsulation: ViewEncapsulation.None, // todo check
})
export class VATRatesComponent implements OnInit {
  public page: IPageResult<IVATRate> = {
    items: [],
    total: 0,
  };

  public filter: Subject<IVATRateFilter>;
  public filterVal: IVATRateFilter;
  public isLoaded: boolean = false;
  public displayDialog: boolean = false;
  public editingItem: IVATRate;
  public selectedItem: IVATRate;
  public newvatrate: boolean = false;
  public filterString: string;
  public newVATRateForm: UntypedFormGroup;
  public vatRate: UntypedFormControl;
  public dateToApplyRate: UntypedFormControl;
  public minDateValue: Date;

  constructor(private vatRateService: VATRateService, public inputHelper: InputHelper) {}

  ngOnInit() {
    this.minDateValue = new Date();

    this.initDefaultFilter();
    this.filter = new Subject<IVATRateFilter>();

    // subscribe to filter changes with a debounce time
    this.filter.pipe(debounceTime(500)).subscribe((f) => {
      this.load(f);
      this.filterVal = f;
    });

    // set default filter
    this.filter.next(this.filterVal);

    // set formGroup instance
    //const regexp = new RegExp(/^(?!\.?$)\d{0,6}(\.\d{0,2})?$/)//(/^\d+(\.\d+)?$/);
    const reg = new RegExp(/^(?:100(?:\.00?)?|\d?\d(?:\.\d\d?)?)$/);
    this.vatRate = new UntypedFormControl(
      '',
      Validators.compose([
        Validators.required,
        Validators.min(0.01),
        Validators.max(99.99),
        Validators.pattern(reg),
      ]),
    );

    var dateValFunc = function () {
      return (validator: UntypedFormControl) => {
        if (!validator.value) {
          return { valid: false, error: 'Is required' };
        }

        var currentDate = new Date().setHours(0, 0, 0, 0);
        var dt = new Date(validator.value).setHours(0, 0, 0, 0);

        if (dt < currentDate) {
          validator.markAsDirty();
          return { valid: false, error: `This value must be greater than current date` };
        }

        return null;
      };
    };
    this.dateToApplyRate = new UntypedFormControl('', dateValFunc());

    this.newVATRateForm = new UntypedFormGroup({
      vatRate: this.vatRate,
      dateToApplyRate: this.dateToApplyRate,
    });
  }

  load(filter: IVATRateFilter) {
    this.vatRateService.getPage(filter).then((result) => {
      this.page.items = result.items;
      this.page.total = result.total;
      this.isLoaded = true;
    });
  }

  showDialogToAdd() {
    this.newvatrate = true;
    this.editingItem = {
      vatRate: null,
      dateToApplyRate: '',
      id: 0,
    };
    this.resetFormValues(this.editingItem);
    this.displayDialog = true;
  }

  save(formValues) {
    this.editingItem.vatRate = formValues.vatRate;
    this.editingItem.dateToApplyRate = moment(formValues.dateToApplyRate).format(
      'YYYY-MM-DD HH:mm',
    );
    console.log(formValues.dateToApplyRate);
    this.vatRateService.save(this.editingItem, this.newvatrate).then((res) => {
      this.editingItem = null;
      this.load(this.filterVal);
    });
    this.displayDialog = false;
  }

  close() {
    this.displayDialog = false;
    this.editingItem = null;
  }

  resetFormValues(formValues) {
    this.newVATRateForm.setValue({
      vatRate: formValues.vatRate,
      dateToApplyRate: formValues.dateToApplyRate
        ? new Date(Date.parse(formValues.dateToApplyRate))
        : null,
    });
  }

  onRowDblclick(item) {
    this.onEdit(item);
  }

  onEdit(rate) {
    this.newvatrate = false;
    this.editingItem = this.cloneVatRate(rate);
    this.resetFormValues(this.editingItem);
    this.displayDialog = true;
  }

  onDelete() {
    if (this.selectedItem) {
      this.vatRateService.delete(this.selectedItem.id).then((res) => {
        this.load(this.filterVal);
        this.selectedItem = null;
      });
    }
  }

  cloneVatRate(c: IVATRate): IVATRate {
    let vatrate = { id: 0, vatRate: 0.0, dateToApplyRate: '' };
    for (let prop in c) {
      vatrate[prop] = c[prop];
    }
    return vatrate;
  }

  onGridLazyLoad(event: LazyLoadEvent) {
    // map grid lazy load event to filter
    //var eventString = JSON.stringify(event);
    //if (eventString === this.filterString) return;
    //console.log(event);
    //this.filter.next(flatten(event));

    this.filterVal.first = event.first;
    this.filterVal.rows = event.rows;
    this.filterVal.sortField = event.sortField;
    this.filterVal.sortOrder = event.sortOrder;

    this.filter.next(this.filterVal);
  }

  initDefaultFilter() {
    this.filterVal = {
      first: 0,
      rows: 10,
      sortField: 'Name', // ASC
      sortOrder: 1,
      filters: {
        vatRate: {},
        dateToApplyRate: {},
      },
    };
  }

  clearFilter(dt: Table) {
    dt.sortField = '';
    dt.rows = 10;
    this.initDefaultFilter();
    this.filter.next(this.filterVal);
  }

  search() {
    this.filterVal.first = 0; //fix error - when you set page > 1 and filter, then data not display
    console.log(this.filterVal);
    this.filter.next(this.filterVal);
  }

  isAlreadyStarted(rate: IVATRate) {
    if (!rate || !rate.dateToApplyRate) {
      return false;
    }

    var currentDate = new Date();
    currentDate.setHours(0, 0, 0, 0);
    var dateToApplyRate = new Date(rate.dateToApplyRate);
    dateToApplyRate.setHours(0, 0, 0, 0);

    return currentDate >= dateToApplyRate;
  }

  //inputErrorToShow(c: any): string {
  //    return !c.valid && c.dirty && c._errors && c._errors.error ? c._errors.error : null;
  //}
}
